import { ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { LogOut, User, ChevronDown, Lock, Bug } from "lucide-react";
import { ReportIssueModal, trackRoute } from "@/components/ReportIssueModal";
import { Badge } from "@/components/ui/badge";
import { ROLE_LABEL, roleBadgeClass, roleLabel, roleWithSummary } from "@/lib/roleLabels";
import { useAuth } from "@/contexts/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ContextualHelpButton } from "@/components/ContextualHelpButton";
import { FirstTimeHint } from "@/components/FirstTimeHint";
import { OnboardingTour, useTourManager } from "@/components/OnboardingTour";
import { RoleChangeBanner } from "@/components/RoleChangeBanner";
import { PaymentFailureBanner } from "@/components/PaymentFailureBanner";
import { SampleDataBanner } from "@/components/sample/SampleDataBanner";
import { ExploreModeProvider } from "@/components/explore/ExploreModeProvider";
import { ExploreBanner } from "@/components/explore/ExploreBanner";

import { PlatformFooter } from "@/components/PlatformFooter";
import { Helmet } from "react-helmet-async";
import { useRatioBreachWatcher } from "@/hooks/useRatioBreachWatcher";
import { useOperatingHours } from "@/hooks/useOperatingHours";
import { usePreserveScrollOnRefocus } from "@/hooks/usePreserveScrollOnRefocus";
import { useSessionGuard } from "@/hooks/useSessionGuard";
import { DeviceTrustGate } from "@/components/auth/DeviceTrustGate";
import { useTrialAccess } from "@/hooks/useTrialAccess";
import { LapsedViewOnly } from "@/components/billing/LapsedViewOnly";

import { ManagerBreakCoverAlerts } from "@/components/break/ManagerBreakCoverAlerts";
import { NurserySwitcherMenu } from "@/components/NurserySwitcherMenu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface LayoutProps {
  children: ReactNode;
}

const roleLabels = ROLE_LABEL as Record<string, string>;

const roleBadgeColors: Record<string, string> = {
  super_admin: "bg-nursly-forest text-white",
  admin: "bg-nursly-sage-light text-[#1A4731]",
  editor: "bg-[#ECFDF5] text-nursly-forest",
  viewer: "bg-nursly-stone text-nursly-muted",
};

// Context to share tour manager with sidebar
export const TourContext = createContext<{ startTour: (fromStep?: string) => void }>({
  startTour: () => {},
});

export const useTourContext = () => useContext(TourContext);

function LayoutBackgroundEffects() {
  // Pushes the nursery's configured opening/closing hours into shiftTypes
  // so shift classification (Starter/Finisher/Midday/Full Day) and the
  // Rota legend reflect this nursery's actual hours rather than defaults.
  useOperatingHours();
  return null;
}

function RatioBreachWatcherEffect() {
  useRatioBreachWatcher();
  return null;
}

export function Layout({ children }: LayoutProps) {
  const trial = useTrialAccess();
  const { profile, role, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // B1-03: keep the scroll position when the tab regains focus/visibility.
  const mainRef = useRef<HTMLElement | null>(null);
  const getMain = useCallback(() => mainRef.current, []);
  usePreserveScrollOnRefocus(getMain, location.pathname);
  const displayName = profile ? `${profile.first_name} ${profile.last_name}`.trim() || profile.email : "User";
  const { showTour, replayStep, deltaKey, startTour, closeTour } = useTourManager();
  const [reportOpen, setReportOpen] = useState(false);
  // B4: keep sign-ins proportionate: device answer, role-based maximum, 8h idle.
  useSessionGuard();

  useEffect(() => { trackRoute(location.pathname); }, [location.pathname]);

  return (
    <ExploreModeProvider>
    <TourContext.Provider value={{ startTour }}>
      <DeviceTrustGate />
      <ErrorBoundary sectionName="Layout Background Effects" level="widget" fallback={null}>
        <LayoutBackgroundEffects />
      </ErrorBoundary>
      <ErrorBoundary sectionName="Ratio Breach Watcher" level="widget" fallback={null}>
        <RatioBreachWatcherEffect />
      </ErrorBoundary>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <ErrorBoundary sectionName="Sidebar" level="widget">
            <AppSidebar />
          </ErrorBoundary>
          <div className="flex-1 flex flex-col min-w-0">
            <PaymentFailureBanner />
{trial.data?.access === "lapsed" && <LapsedViewOnly mainRef={mainRef} />}
            <ErrorBoundary sectionName="Sample Data Banner" level="widget" fallback={null}>
              <SampleDataBanner />
            </ErrorBoundary>
            <ErrorBoundary sectionName="Explore Banner" level="widget" fallback={null}>
              <ExploreBanner />
            </ErrorBoundary>

            <ErrorBoundary sectionName="Top Navigation" level="widget">
              <header className="h-14 border-b border-[rgba(13,31,20,0.10)] bg-white flex items-center justify-between px-4 shrink-0">
                <div className="flex items-center gap-3">
                  <SidebarTrigger />
                </div>
                <div className="flex items-center gap-2">

                  <ContextualHelpButton pathname={location.pathname} search={location.search} />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-tour="user-menu" className="flex items-center gap-2 hover:opacity-80 transition-opacity outline-none">
                        <UserAvatar
                          photoUrl={profile?.profile_photo_url || null}
                          firstName={profile?.first_name || ""}
                          lastName={profile?.last_name || ""}
                          size={36}
                        />
                        <div className="hidden sm:flex flex-col items-start">
                          <span className="text-sm font-medium font-body leading-tight text-nursly-ink">{displayName}</span>
                          {role && (
                            <span title={roleWithSummary(role)}>
                              <Badge
                                aria-label={roleWithSummary(role)}
                                className={`${roleBadgeClass(role)} text-[9px] px-1.5 py-0 h-4 leading-none`}
                              >
                                {roleLabel(role)}
                              </Badge>
                            </span>
                          )}
                        </div>
                        <ChevronDown className="h-3.5 w-3.5 text-nursly-faint hidden sm:block" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuLabel className="font-normal text-xs text-nursly-faint font-body">
                        Hi, {profile?.first_name || "there"}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <NurserySwitcherMenu />
                      <DropdownMenuItem onClick={() => navigate("/my-profile")} className="cursor-pointer font-body">
                        <User className="h-4 w-4 mr-2" /> My Profile
                      </DropdownMenuItem>
                      {(role === "admin" || role === "super_admin") && (
                        <DropdownMenuItem onClick={() => navigate("/my-profile?tab=security")} className="cursor-pointer font-body">
                          <Lock className="h-4 w-4 mr-2" /> Change passcode
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setReportOpen(true)} className="cursor-pointer font-body">
                        <Bug className="h-4 w-4 mr-2" /> Report an issue
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={signOut} className="cursor-pointer text-destructive font-body">
                        <LogOut className="h-4 w-4 mr-2" /> Log Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </header>
            </ErrorBoundary>
            <main ref={mainRef} className="flex-1 overflow-auto p-4 md:p-6 bg-nursly-cream">
              <RoleChangeBanner />
              <FirstTimeHint pathname={location.pathname} />
              {children}
            </main>
            <PlatformFooter />
          </div>
        </div>
      </SidebarProvider>
      {showTour && <OnboardingTour replayFromStep={replayStep} deltaKey={deltaKey} onClose={closeTour} />}
      <ReportIssueModal open={reportOpen} onOpenChange={setReportOpen} />
      <ErrorBoundary sectionName="Break Cover Alerts" level="widget" fallback={null}>
        <ManagerBreakCoverAlerts />
      </ErrorBoundary>
    </TourContext.Provider>
    </ExploreModeProvider>
  );
}
